// Game State Types for DEFCON TERMINAL

import type { OutcomeType, PathType, ScenarioState } from './scenario';

export interface ScoreBreakdown {
  basePoints: number;
  decisionPoints: number;
  intelligencePoints: number;
  easterEggPoints: number;
  complexityMultiplier: number;
  pathModifier: number;
  outcomeMultiplier: number;
  bonuses: { name: string; points: number }[];
  penalties: { name: string; points: number }[];
  subtotal: number;
  total: number;
}

export interface CompletedScenario {
  scenarioId: string;
  path: PathType;
  outcomeType: OutcomeType;
  score: number;
  scoreBreakdown: ScoreBreakdown;
  decisionsCount: number;
  playTimeSeconds: number;
  completedAt: number;
  casualties?: number;
  warheadsUsed?: number;
}

export interface LeaderboardEntry {
  rank: number;
  handle: string;
  score: number;
  path: PathType;
  scenario: string;
  outcomeType: OutcomeType;
  completedAt: number;
}

export interface GlobalStatistics {
  totalGamesPlayed: number;
  totalNuclearWars: number;
  totalPeacefulResolutions: number;
  totalCovertSuccesses: number;
  totalCasualties: number;
  mostPopularPath: PathType;
  mostCommonOutcome: OutcomeType;
  averageDecisionsPerGame: number;
}

// Runtime game state (in-memory during gameplay)
export interface RuntimeGameState {
  isPlaying: boolean;
  currentScenario: string | null;
  currentNode: string | null;
  scenarioState: ScenarioState | null;
  decisionHistory: {
    nodeId: string;
    choiceId: string;
    timestamp: number;
    stateSnapshot: ScenarioState;
  }[];
  startTime: number | null;
  lastSaveTime: number | null;
}

// Bonus/Penalty definitions
export const BONUSES = {
  PEACEFUL_DIPLOMAT: { name: 'Peaceful Diplomat', points: 200, description: 'Resolved crisis without nuclear weapons' },
  SHADOW_OPERATIVE: { name: 'Shadow Operative', points: 150, description: 'Completed covert mission undetected' },
  HISTORIAN: { name: 'Historian', points: 100, description: 'Discovered all historical references' },
  COMPLETIONIST: { name: 'Completionist', points: 500, description: 'Completed all four pathways' },
  SPEEDRUN: { name: 'Speed Runner', points: 100, description: 'Completed scenario under time threshold' },
  MERCIFUL: { name: 'Merciful', points: 150, description: 'Minimized casualties' },
  RUTHLESS: { name: 'Ruthless Efficiency', points: 150, description: 'Maximum efficiency achieved' },
  DEESCALATOR: { name: 'De-escalator', points: 175, description: 'Lowered DEFCON without conflict' },
  BACKDOOR_FINDER: { name: 'Backdoor Finder', points: 75, description: 'Found hidden command' },
} as const;

export const PENALTIES = {
  EXTINCTION_EVENT: { name: 'Extinction Event', points: -500, description: 'Triggered full thermonuclear war' },
  BLOWN_COVER: { name: 'Blown Cover', points: -100, description: 'Compromised covert operation' },
  CIVILIAN_CASUALTIES: { name: 'Civilian Casualties', points: -10, description: 'Per 1000 casualties' },
  ALLY_BETRAYAL: { name: 'Ally Betrayal', points: -200, description: 'Actions harmed allied nations' },
  UNNECESSARY_ESCALATION: { name: 'Unnecessary Escalation', points: -75, description: 'Escalated when de-escalation was possible' },
} as const;

export type BonusType = keyof typeof BONUSES;
export type PenaltyType = keyof typeof PENALTIES;

// Path modifiers
export const PATH_MODIFIERS: Record<PathType, number> = {
  'sarn-west': 1.0,
  'sarn-east': 1.1,
  'phantom-directive': 1.2,
  'red-shadow': 1.2,
};

// Calculate complexity multiplier
export function calculateComplexityMultiplier(
  uniqueDecisions: number,
  branchesExplored: number
): number {
  return 1.0 + (uniqueDecisions * 0.05) + (branchesExplored * 0.1);
}

// Calculate final score
export function calculateScore(
  basePoints: number,
  complexityMultiplier: number,
  pathModifier: number,
  outcomeMultiplier: number,
  bonuses: BonusType[],
  penalties: { type: PenaltyType; count?: number }[]
): ScoreBreakdown {
  const bonusDetails = bonuses.map(b => ({
    name: BONUSES[b].name,
    points: BONUSES[b].points,
  }));

  const penaltyDetails = penalties.map(p => ({
    name: PENALTIES[p.type].name,
    points: PENALTIES[p.type].points * (p.count || 1),
  }));

  const subtotal = basePoints * complexityMultiplier * pathModifier * outcomeMultiplier;
  const bonusTotal = bonusDetails.reduce((sum, b) => sum + b.points, 0);
  const penaltyTotal = penaltyDetails.reduce((sum, p) => sum + p.points, 0);
  const total = Math.max(0, Math.round(subtotal + bonusTotal + penaltyTotal));

  return {
    basePoints,
    decisionPoints: 0,
    intelligencePoints: 0,
    easterEggPoints: 0,
    complexityMultiplier,
    pathModifier,
    outcomeMultiplier,
    bonuses: bonusDetails,
    penalties: penaltyDetails,
    subtotal: Math.round(subtotal),
    total,
  };
}
